/**
 * TrainerOverviewV2Page — /training/trainer-overview-v2
 * Per-trainer (or All-Trainers) deep-dive dashboard.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  TrendingUp, TrendingDown, Users, Calendar, Award, IndianRupee,
  Loader2, ChevronRight, ExternalLink, RefreshCw, PieChart, PhoneCall,
} from 'lucide-react';
import { PieChart as RePieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend } from 'recharts';
import { toast } from 'sonner';
import api from '../../lib/api';
import { CalendarDays, Filter } from 'lucide-react';
import NewTrainerOverviewPage from './NewTrainerOverviewPage';

/* ─── Reusable date-preset popover ─────────────────────── */
function DatePresetPopover({ label, preset, setPreset, custom, setCustom, currentLabel, testid }) {
  const [open, setOpen] = useState(false);
  const clear = () => setCustom({ month: '', quarter: '', from: '', to: '' });
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1 ${preset !== 'all' && preset !== 'this_month' ? 'bg-violet-50 text-violet-700 border-violet-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
        data-testid={testid}>
        <CalendarDays className="w-3 h-3" /> {label}: <b>{currentLabel}</b>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-xl p-3 w-[360px]" data-testid={`${testid}-menu`}>
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {[['all', 'All time'], ['this_month', 'This month'], ['last_month', 'Last month'], ['fy26', 'FY 26'], ['fy25', 'FY 25'], ['ytd', 'YTD']].map(([k, l]) => (
                <button key={k} onClick={() => { setPreset(k); clear(); }}
                  className={`px-2 py-1 rounded-md border text-[11px] ${preset === k ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`${testid}-${k}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-1.5">
              <label className={`flex items-center justify-between gap-2 px-2 py-1 rounded-md border text-[11px] ${preset === 'month' ? 'bg-violet-50 border-violet-300' : 'border-slate-200'}`}>
                <span className="text-slate-500">Choose month</span>
                <input type="month" value={custom.month || ''} onChange={(e) => { setCustom({ ...custom, month: e.target.value, quarter: '', from: '', to: '' }); setPreset('month'); }} className="text-[10px] bg-transparent focus:outline-none" data-testid={`${testid}-month`} />
              </label>
              <label className={`flex items-center justify-between gap-2 px-2 py-1 rounded-md border text-[11px] ${preset === 'quarter' ? 'bg-violet-50 border-violet-300' : 'border-slate-200'}`}>
                <span className="text-slate-500">Choose quarter</span>
                <select value={custom.quarter || ''} onChange={(e) => { setCustom({ ...custom, quarter: e.target.value, month: '', from: '', to: '' }); setPreset('quarter'); }} className="text-[10px] bg-transparent focus:outline-none" data-testid={`${testid}-quarter`}>
                  <option value="">Select…</option>
                  {['2026-Q1','2026-Q2','2026-Q3','2026-Q4','2025-Q1','2025-Q2','2025-Q3','2025-Q4'].map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </label>
              <div className={`flex items-center justify-between gap-1 px-2 py-1 rounded-md border text-[11px] ${preset === 'custom' ? 'bg-violet-50 border-violet-300' : 'border-slate-200'}`}>
                <span className="text-slate-500">Custom range</span>
                <div className="flex items-center gap-1">
                  <input type="date" value={custom.from || ''} onChange={(e) => { setCustom({ ...custom, from: e.target.value, month: '', quarter: '' }); setPreset('custom'); }} className="text-[10px] bg-transparent focus:outline-none" data-testid={`${testid}-custom-from`} />
                  <span>→</span>
                  <input type="date" value={custom.to || ''} onChange={(e) => { setCustom({ ...custom, to: e.target.value, month: '', quarter: '' }); setPreset('custom'); }} className="text-[10px] bg-transparent focus:outline-none" data-testid={`${testid}-custom-to`} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Reusable Include/Exclude popover ─────────────────── */
function IncExcPopover({ label, options, include, exclude, setInclude, setExclude, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const active = include.length + exclude.length;
  const filtered = options.filter(o => o.toLowerCase().includes(q.toLowerCase()));
  const state = (o) => include.includes(o) ? 'inc' : exclude.includes(o) ? 'exc' : 'off';
  const cycle = (o) => {
    const s = state(o);
    if (s === 'off') setInclude([...include, o]);
    else if (s === 'inc') { setInclude(include.filter(x => x !== o)); setExclude([...exclude, o]); }
    else setExclude(exclude.filter(x => x !== o));
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1 ${active ? 'bg-violet-50 text-violet-700 border-violet-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
        data-testid={testid}>
        <Filter className="w-3 h-3" /> {label}{active > 0 && <span className="ml-1 bg-violet-600 text-white px-1 py-0 rounded text-[9px]">{active}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-xl w-[280px] max-h-[380px] flex flex-col">
            <div className="p-2 border-b">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full text-[11px] px-2 py-1 border rounded" />
              <div className="flex justify-between items-center mt-1 text-[10px] text-slate-500">
                <span>OFF → Include → Exclude → OFF</span>
                {active > 0 && <button onClick={() => { setInclude([]); setExclude([]); }} className="text-rose-600 font-medium">Clear</button>}
              </div>
            </div>
            <div className="flex-1 overflow-auto py-1">
              {filtered.map(o => {
                const s = state(o);
                return (
                  <button key={o} onClick={() => cycle(o)} className="w-full flex items-center justify-between px-2 py-1 text-[11px] hover:bg-slate-50" data-testid={`${testid}-opt-${o}`}>
                    <span className="truncate text-slate-700">{o}</span>
                    {s === 'inc' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px]">Include ✓</Badge>}
                    {s === 'exc' && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[9px]">Exclude ✕</Badge>}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const inrFull = (n) => n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`;
const pad = (x) => String(x).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth()+1, 0);
const buildDateRange = (preset, custom = {}) => {
  const t = new Date();
  if (preset === 'this_month') return { from: toISO(startOfMonth(t)), to: toISO(t), label: 'This month' };
  if (preset === 'last_month') { const d = new Date(t.getFullYear(), t.getMonth()-1, 1); return { from: toISO(startOfMonth(d)), to: toISO(endOfMonth(d)), label: 'Last month' }; }
  if (preset === 'fy26') return { from: '2026-04-01', to: '2027-03-31', label: 'FY 26' };
  if (preset === 'fy25') return { from: '2025-04-01', to: '2026-03-31', label: 'FY 25' };
  if (preset === 'ytd') return { from: `${t.getFullYear()}-01-01`, to: toISO(t), label: 'YTD' };
  if (preset === 'all') return { from: '', to: '', label: 'All time' };
  if (preset === 'month' && custom.month) { const [y,m] = custom.month.split('-').map(Number); const d = new Date(y, m-1, 1); return { from: toISO(startOfMonth(d)), to: toISO(endOfMonth(d)), label: `${custom.month}` }; }
  if (preset === 'quarter' && custom.quarter) { const [y,q] = custom.quarter.split('-Q').map(Number); const sm = (q-1)*3; return { from: toISO(new Date(y, sm, 1)), to: toISO(new Date(y, sm+3, 0)), label: `Q${q} ${y}` }; }
  if (preset === 'custom' && custom.from && custom.to) return { from: custom.from, to: custom.to, label: `${custom.from} → ${custom.to}` };
  return { from: toISO(startOfMonth(t)), to: toISO(t), label: 'This month' };
};

function DeltaBadge({ pct }) {
  if (pct == null) return <span className="text-[10px] text-slate-300">—</span>;
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      <Icon className="w-3 h-3" /> {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function KpiCard({ label, value, delta, sub, Icon, testid }) {
  return (
    <Card className="p-4 border" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">{label}</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
          {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
        </div>
        {Icon && <div className="p-2 rounded-lg bg-violet-50"><Icon className="w-4 h-4 text-violet-600" /></div>}
      </div>
      {delta != null && <div className="mt-2 flex items-center gap-1"><DeltaBadge pct={delta} /><span className="text-[10px] text-slate-400">vs prev period</span></div>}
    </Card>
  );
}

function DemoDetailModal({ demo, open, onClose }) {
  if (!demo) return null;
  const ZOHO_ORG = '699857543';
  const CRM_ORG = 'org60036129527';
  const crmUrl = demo.lead_id ? `https://crm.zoho.in/crm/${CRM_ORG}/tab/Leads/${demo.lead_id}` : null;
  const booksUrl = demo.zoho_customer_id ? `https://books.zoho.com/app/${ZOHO_ORG}/#/customers/${demo.zoho_customer_id}` : null;
  const batchUrl = demo.batch_id ? `/training/batches/${demo.batch_id}` : null;
  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[150px_1fr] gap-x-4 py-1 text-[12px] items-baseline">
      <span className="text-slate-400">{label}:</span>
      <span className="text-slate-800 font-medium">{children || <span className="text-slate-300 italic font-normal">—</span>}</span>
    </div>
  );
  const statusTone = ({ Sucessful: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Failed: 'bg-rose-50 text-rose-700 border-rose-200',
    Pending: 'bg-amber-50 text-amber-700 border-amber-200' })[demo.demo_status] || 'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="v2-demo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <PhoneCall className="w-4 h-4 text-violet-600" />
            {demo.full_name} · {demo.date_of_demo}
            <Badge className={`ml-1 border text-[10px] ${statusTone}`}>{demo.demo_status}</Badge>
            {demo.confirmed_admission && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Admission ✓</Badge>}
            {(demo.rejected||'').toLowerCase()==='yes' && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">Rejected</Badge>}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[10px] uppercase font-semibold text-slate-400 mt-2 mb-1">Demo</p>
        <div className="border-t border-slate-100 pt-1">
          <Row label="Date of Demo">{demo.date_of_demo}</Row>
          <Row label="Demo Status">{demo.demo_status}</Row>
          <Row label="Tech Call Demo By">{demo.trainer_name}</Row>
          <Row label="Confirmed Admission">{demo.confirmed_admission ? 'Yes' : 'No'}</Row>
          <Row label="Action Time">{demo.action_time_feedback}</Row>
          <Row label="Action Taken At">{demo.action_taken_at}</Row>
          <Row label="Action Suggested">{demo.action_suggested}</Row>
          <Row label="Action Time (min)">{demo.action_time_minutes}</Row>
        </div>
        <p className="text-[10px] uppercase font-semibold text-slate-400 mt-3 mb-1">Lead</p>
        <div className="border-t border-slate-100 pt-1">
          <Row label="Name">{demo.full_name}</Row>
          <Row label="Enrollment No">{demo.enrollment_no}</Row>
          <Row label="Phone">{demo.phone}</Row>
          <Row label="Email">{demo.email}</Row>
          <Row label="Owner">{demo.owner_name}</Row>
          <Row label="Technology">{demo.technology}</Row>
          <Row label="Batch #">{demo.batch_number}</Row>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-slate-100">
          {crmUrl && <a href={crmUrl} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-1 rounded border border-blue-300 bg-blue-50 text-blue-700 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />CRM Lead</a>}
          {booksUrl && <a href={booksUrl} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-1 rounded border border-violet-300 bg-violet-50 text-violet-700 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />Zoho Books Customer</a>}
          {batchUrl && <a href={batchUrl} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-1 rounded border border-violet-300 bg-violet-50 text-violet-700 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />Open Batch</a>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DemosListModal({ demos, open, onClose, onPick }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="v2-demos-list-modal">
        <DialogHeader><DialogTitle>Demos ({demos.length})</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-auto border rounded">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 border-b sticky top-0">
              <tr className="text-left text-slate-500">
                <th className="px-2 py-1.5 font-medium">Date</th>
                <th className="px-2 py-1.5 font-medium">Lead</th>
                <th className="px-2 py-1.5 font-medium">Trainer</th>
                <th className="px-2 py-1.5 font-medium">Status</th>
                <th className="px-2 py-1.5 font-medium">Admission</th>
                <th className="px-2 py-1.5 w-6"></th>
              </tr>
            </thead>
            <tbody>
              {demos.map(d => (
                <tr key={d.lead_id} onClick={() => onPick(d)}
                  className="border-b border-slate-100 hover:bg-violet-50 cursor-pointer" data-testid={`v2-demo-row-${d.lead_id}`}>
                  <td className="px-2 py-1.5 font-mono text-slate-500">{d.date_of_demo}</td>
                  <td className="px-2 py-1.5 text-slate-700 font-medium">{d.full_name}</td>
                  <td className="px-2 py-1.5 text-slate-600">{d.trainer_name}</td>
                  <td className="px-2 py-1.5"><Badge className="text-[10px]">{d.demo_status}</Badge></td>
                  <td className="px-2 py-1.5">{d.confirmed_admission ? <span className="text-emerald-600">✓</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-1.5 text-slate-300"><ChevronRight className="w-3 h-3" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const DONUT_COLORS = { Sucessful: '#10b981', Failed: '#f43f5e', Pending: '#f59e0b', Unknown: '#94a3b8',
                       Running: '#8b5cf6', Completed: '#0ea5e9', Upcoming: '#f59e0b', Admissions: '#10b981',
                       Rejected: '#f43f5e', NoAction: '#94a3b8' };

function Donut({ data, testid, height = 200 }) {
  const total = data.reduce((a, b) => a + b.value, 0);
  if (!total) return <div className="h-40 flex items-center justify-center text-slate-400 text-xs italic" data-testid={testid}>No data in window</div>;
  return (
    <ResponsiveContainer width="100%" height={height} data-testid={testid}>
      <RePieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
          {data.map((e, i) => <Cell key={i} fill={DONUT_COLORS[e.name] || '#a78bfa'} />)}
        </Pie>
        <ReTooltip formatter={(v, n) => [`${v}`, n]} />
        <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
      </RePieChart>
    </ResponsiveContainer>
  );
}

export default function TrainerOverviewV2Page() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [trainer, setTrainer] = useState('all');
  // KPI window (drives demos, revenue, kpi deltas)
  const [datePreset, setDatePreset] = useState('this_month');
  const [dateCustom, setDateCustom] = useState({ month: '', quarter: '', from: '', to: '' });
  // Batch start-date window (drives NTO + V2 batch universe)
  const [startPreset, setStartPreset] = useState('all');
  const [startCustom, setStartCustom] = useState({ month: '', quarter: '', from: '', to: '' });
  // Category (multi-select) — feeds both V2 and NTO
  const [catInc, setCatInc] = useState([]);
  const [catExc, setCatExc] = useState([]);
  const [showDemosList, setShowDemosList] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const dateRange = useMemo(() => buildDateRange(datePreset, dateCustom), [datePreset, dateCustom]);
  const startRange = useMemo(() => buildDateRange(startPreset, startCustom), [startPreset, startCustom]);

  const load = () => {
    setLoading(true);
    return api.get('/training/reports/trainer-overview-v2', { params: {
      trainer: trainer === 'all' ? '' : trainer,
      date_from: dateRange.from,
      date_to: dateRange.to,
      start_from: startRange.from || undefined,
      start_to: startRange.to || undefined,
      categories_include: catInc.length ? catInc.join(',') : undefined,
    } }).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [trainer, dateRange.from, dateRange.to, startRange.from, startRange.to, catInc.join(',')]);

  // If the currently-picked trainer is inactive and the show-inactive toggle
  // is off, reset back to "all" so the user isn't stuck on a hidden option.
  useEffect(() => {
    if (!data?.trainer_options || trainer === 'all' || showInactive) return;
    const picked = data.trainer_options.find(t => t.name === trainer);
    if (picked && !picked.is_active) setTrainer('all');
  }, [data, trainer, showInactive]);

  const refreshDemos = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/training/crm/demos/sync?incremental=true');
      toast.success(`Synced · ${r.data.demo_records || 0} demos updated`);
      await load();
    } catch (e) { toast.error('Sync failed'); }
    finally { setRefreshing(false); }
  };

  const k = data?.kpis || {};
  const df = data?.demo_funnel || {};
  const rev = data?.revenue_summary || {};
  const ss = data?.students_summary || {};
  const demoDonut = useMemo(() => ([
    { name: 'Sucessful', value: df.sucessful || 0 },
    { name: 'Failed', value: df.failed || 0 },
    { name: 'Pending', value: df.pending || 0 },
    { name: 'Unknown', value: df.unknown || 0 },
  ].filter(x => x.value > 0)), [df]);
  const revDonut = useMemo(() => ([
    { name: 'Running', value: rev.running || 0 },
    { name: 'Completed', value: rev.completed || 0 },
    { name: 'Upcoming', value: rev.upcoming || 0 },
  ].filter(x => x.value > 0)), [rev]);

  return (
    <div className="space-y-4 pb-8" data-testid="trainer-overview-v2-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Trainer Overview V2 <Badge className="ml-2 text-[9px] bg-violet-100 text-violet-700 border-violet-200">Per-trainer deep dive</Badge></h1>
          <p className="text-sm text-slate-400 mt-0.5">Demos · batches · revenue · student pipeline — {dateRange.label}{startRange.from ? ` · Started in ${startRange.label}` : ''}{catInc.length ? ` · ${catInc.length} categor${catInc.length===1?'y':'ies'}` : ''}</p>
        </div>
        <Button size="sm" variant="outline" onClick={refreshDemos} disabled={refreshing} className="text-[11px]" data-testid="v2-refresh-demos">
          {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Refresh CRM demos
        </Button>
      </div>

      {/* Unified filter bar */}
      <Card className="p-3 border sticky top-0 z-30 bg-white/95 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          <label className="flex items-center gap-1">
            <span className="text-slate-500 font-semibold">Trainer:</span>
            <select value={trainer} onChange={(e) => setTrainer(e.target.value)}
              className="text-[11px] border rounded px-2 py-1 min-w-[220px]" data-testid="v2-trainer-picker">
              <option value="all">All trainers (aggregate)</option>
              {(data?.trainer_options || (data?.trainers || []).map(t => ({ name: t, is_active: true })))
                .filter(t => showInactive || t.is_active)
                .map(t => <option key={t.name} value={t.name}>{t.name}{t.is_active ? '' : ' · inactive'}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-1 text-slate-500" title="Inactive = no Running or Upcoming batch">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
              className="w-3 h-3 accent-violet-600" data-testid="v2-show-inactive" />
            <span>Show inactive trainers</span>
            {data?.trainer_options && (
              <span className="text-[9px] text-slate-400">
                ({data.trainer_options.filter(t => t.is_active).length} active / {data.trainer_options.length} total)
              </span>
            )}
          </label>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <DatePresetPopover label="Date" preset={datePreset} setPreset={setDatePreset}
              custom={dateCustom} setCustom={setDateCustom}
              currentLabel={dateRange.label} testid="v2-date-preset" />
            <DatePresetPopover label="Started in" preset={startPreset} setPreset={setStartPreset}
              custom={startCustom} setCustom={setStartCustom}
              currentLabel={startRange.label} testid="v2-start-preset" />
            <IncExcPopover label="Category" options={data?.categories || []}
              include={catInc} exclude={catExc}
              setInclude={setCatInc} setExclude={setCatExc}
              testid="v2-category-filter" />
            {loading && <Loader2 className="w-3 h-3 animate-spin text-violet-500" />}
          </div>
        </div>
      </Card>

      {/* 5 KPI cards */}
      <div className="grid grid-cols-5 gap-3">
        <button onClick={() => setShowDemosList(true)} className="text-left" data-testid="v2-kpi-demos">
          <KpiCard label="Total Demos" value={k.total_demos?.now ?? '—'} delta={k.total_demos?.delta_pct}
            sub={`${df.sucessful||0} Sucessful · ${df.failed||0} Failed · ${df.pending||0} Pending`}
            Icon={PhoneCall} />
        </button>
        <KpiCard label="Total Students" value={k.total_students?.now ?? '—'}
          sub={`${ss.active||0} active · ${ss.dropped||0} dropped`} Icon={Users} testid="v2-kpi-students" />
        <KpiCard label="Running Batches" value={k.running_batches?.now ?? '—'}
          sub={`${k.running_batches?.weekday||0} Weekday · ${k.running_batches?.weekend||0} Weekend`}
          Icon={Calendar} testid="v2-kpi-running" />
        <KpiCard label="Completed Batches" value={k.completed_batches?.now ?? '—'}
          sub={`Total revenue ${inrFull(rev.completed)}`}
          Icon={Award} testid="v2-kpi-completed" />
        <KpiCard label="Total Revenue" value={inrFull(k.total_revenue?.now || 0)} delta={k.total_revenue?.delta_pct}
          sub={`Invoices in window`} Icon={IndianRupee} testid="v2-kpi-revenue" />
      </div>

      {/* Demos overview + Revenue */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border" data-testid="v2-demos-overview">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-slate-700">Demos Overview</p>
            <PieChart className="w-3.5 h-3.5 text-slate-400" />
          </div>
          <Donut data={demoDonut} testid="v2-demo-donut" />
          <div className="grid grid-cols-3 gap-1 mt-2 text-center">
            <div className="p-2 rounded bg-emerald-50"><p className="text-[9px] text-emerald-600 uppercase">Attended</p><p className="text-lg font-bold text-emerald-700">{df.attended||0}</p></div>
            <div className="p-2 rounded bg-blue-50"><p className="text-[9px] text-blue-600 uppercase">Admission</p><p className="text-lg font-bold text-blue-700">{df.confirmed_admission||0}</p></div>
            <div className="p-2 rounded bg-rose-50"><p className="text-[9px] text-rose-600 uppercase">Rejected</p><p className="text-lg font-bold text-rose-700">{df.rejected||0}</p></div>
          </div>
          <p className="text-[10px] text-center text-slate-500 mt-2">Conversion rate: <b className="text-slate-700">{df.conversion_rate_pct||0}%</b></p>
        </Card>

        <Card className="p-4 border" data-testid="v2-students-summary">
          <p className="text-[11px] font-semibold text-slate-700 mb-2">Students Summary</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="p-3 rounded bg-slate-50"><p className="text-[9px] text-slate-500 uppercase">New (window)</p><p className="text-2xl font-bold text-slate-800">{ss.new_in_window||0}</p></div>
            <div className="p-3 rounded bg-emerald-50"><p className="text-[9px] text-emerald-600 uppercase">Total Active</p><p className="text-2xl font-bold text-emerald-700">{ss.active||0}</p></div>
            <div className="p-3 rounded bg-rose-50"><p className="text-[9px] text-rose-600 uppercase">Dropped</p><p className="text-2xl font-bold text-rose-700">{ss.dropped||0}</p></div>
            <div className="p-3 rounded bg-blue-50"><p className="text-[9px] text-blue-600 uppercase">Placement / Job</p><p className="text-2xl font-bold text-blue-700">{ss.placement_or_job_support||0}</p></div>
          </div>
        </Card>

        <Card className="p-4 border" data-testid="v2-revenue-summary">
          <p className="text-[11px] font-semibold text-slate-700 mb-2">Revenue Summary</p>
          <Donut data={revDonut} testid="v2-revenue-donut" />
          <div className="mt-2 grid grid-cols-1 gap-1 text-[10px]">
            <div className="flex justify-between"><span className="text-violet-700">● Running</span><b>{inrFull(rev.running)}</b></div>
            <div className="flex justify-between"><span className="text-sky-700">● Completed</span><b>{inrFull(rev.completed)}</b></div>
            <div className="flex justify-between"><span className="text-amber-700">● Upcoming</span><b>{inrFull(rev.upcoming)}</b></div>
            <div className="flex justify-between border-t pt-1 mt-1"><span className="font-semibold">Total all-time</span><b>{inrFull(rev.total_all_time)}</b></div>
          </div>
        </Card>
      </div>

      {/* Embedded New Trainer Overview (batch-level detail with modals) */}
      <div className="border-t border-slate-200 pt-4 mt-6">
        <NewTrainerOverviewPage embedded
          externalTrainer={trainer !== 'all' ? trainer : null}
          externalStartRange={startRange.from ? { from: startRange.from, to: startRange.to } : null}
          externalCategoriesInclude={catInc} />
      </div>

      <DemosListModal demos={data?.demos || []} open={showDemosList} onClose={() => setShowDemosList(false)}
        onPick={(d) => { setShowDemosList(false); setSelectedDemo(d); }} />
      <DemoDetailModal demo={selectedDemo} open={!!selectedDemo} onClose={() => setSelectedDemo(null)} />
    </div>
  );
}
